/**
 * SLM Deployment Service — wraps /api/admin/ai/slm-deployments/
 * Phase 5.2.1 — WQ-324 (operator face of the WQ-322 lifecycle + WQ-323 serving).
 *
 * The server enforces every gate (require_ml_action tier matrix, the DM-18
 * activation flag, the measured-RAM capability gate). This client only DRIVES
 * those endpoints and reflects their responses honestly — it never reimplements
 * a gate (guidance Triggers #5/#6). SLMApiError carries the HTTP status + the
 * server's structured detail so the console can surface 403 / 501 (DEF-075
 * container-tier, DEF-072 fine-tune) / 422 / 503 distinctly, never as a generic
 * error.
 */

export interface SLMDeployment {
  id: string;
  tenant_id: string;
  model_id: string;
  state: string; // provisioning | active | deactivated | retired | fine_tuning
  deployed_by: string | null;
  state_changed_at: string | null;
  state_changed_by: string | null;
  detail: string | null;
  served_model_name: string | null;
  served_size_class: string | null; // large | small | null
  downgraded: boolean;
  activated_at: string | null;
  retired_at: string | null;
  created_at: string | null;
}

export interface ServingConfig {
  inference_enabled: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

export interface ActivationReadiness {
  deployment_id: string;
  state: string | null;
  activation_flag_enabled: boolean;
  isolation_level: string;
  container_tier_supported: boolean;
  available_ram_mb: number;
  thresholds: { large_min_free_ram_mb: number; small_min_free_ram_mb: number };
  ram_verdict: {
    allowed: boolean;
    size_class: string | null;
    model_name: string | null;
    downgraded: boolean;
    reason: string; // fits_large | downgraded_to_small | insufficient_ram
  };
  origin: { status: string; model_name: string; reason: string | null };
  ollama: {
    reachable: boolean;
    status: string | null;
    models_loaded: number | null;
    target_model_present: boolean;
  };
  served: {
    served_model_name: string | null;
    served_size_class: string | null;
    downgraded: boolean;
    current_tier_fits: boolean | null;
  };
  ready_to_activate: boolean;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

const BASE = '/api/admin/ai/slm-deployments';

/** Error carrying HTTP status + server detail so the UI can branch honestly. */
export class SLMApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'SLMApiError';
    this.status = status;
    this.code = code;
  }
}

async function parseError(res: Response, fallback: string): Promise<SLMApiError> {
  let message = `${fallback} (${res.status})`;
  let code: string | undefined;
  try {
    const body = await res.json();
    const detail = body?.detail;
    if (typeof detail === 'string') {
      message = detail;
    } else if (detail && typeof detail === 'object') {
      // Structured detail: { error: { code, message } } (slm_serving errors)
      const err = detail.error ?? detail;
      message = err?.message ?? message;
      code = err?.code;
    }
  } catch {
    // non-JSON body — keep the fallback message
  }
  return new SLMApiError(message, res.status, code);
}

export async function listDeployments(
  authFetch: FetchFn,
  filters?: { tenant_id?: string; state?: string }
): Promise<SLMDeployment[]> {
  const params = new URLSearchParams();
  if (filters?.tenant_id) params.set('tenant_id', filters.tenant_id);
  if (filters?.state) params.set('state', filters.state);
  const qs = params.toString();
  const res = await authFetch(`${BASE}${qs ? '?' + qs : ''}`);
  if (!res.ok) throw await parseError(res, 'Failed to list SLM deployments');
  return res.json();
}

export async function getDeployment(
  authFetch: FetchFn,
  id: string
): Promise<SLMDeployment> {
  const res = await authFetch(`${BASE}/${id}`);
  if (!res.ok) throw await parseError(res, 'Failed to get SLM deployment');
  return res.json();
}

export async function deploySLM(
  authFetch: FetchFn,
  data: { model_id: string; tenant_id?: string }
): Promise<SLMDeployment> {
  const res = await authFetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw await parseError(res, 'Failed to deploy SLM');
  return res.json();
}

async function transition(
  authFetch: FetchFn,
  id: string,
  action: 'activate' | 'deactivate' | 'retire',
  detail?: string
): Promise<SLMDeployment> {
  const res = await authFetch(`${BASE}/${id}/${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(detail ? { detail } : {}),
  });
  if (!res.ok) throw await parseError(res, `Failed to ${action} deployment`);
  return res.json();
}

export const activateDeployment = (f: FetchFn, id: string, detail?: string) =>
  transition(f, id, 'activate', detail);
export const deactivateDeployment = (f: FetchFn, id: string, detail?: string) =>
  transition(f, id, 'deactivate', detail);
export const retireDeployment = (f: FetchFn, id: string, detail?: string) =>
  transition(f, id, 'retire', detail);

export async function getServingConfig(authFetch: FetchFn): Promise<ServingConfig> {
  const res = await authFetch(`${BASE}/serving-config`);
  if (!res.ok) throw await parseError(res, 'Failed to read serving config');
  return res.json();
}

export async function setServingConfig(
  authFetch: FetchFn,
  inference_enabled: boolean
): Promise<ServingConfig> {
  const res = await authFetch(`${BASE}/serving-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inference_enabled }),
  });
  if (!res.ok) throw await parseError(res, 'Failed to update serving config');
  return res.json();
}

export async function getActivationReadiness(
  authFetch: FetchFn,
  id: string
): Promise<ActivationReadiness> {
  const res = await authFetch(`${BASE}/${id}/activation-readiness`);
  if (!res.ok) throw await parseError(res, 'Failed to check activation readiness');
  return res.json();
}
